import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  ButtonGroup,
  Paper,
  Rating,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useAdminListReviews, useUpdateReview } from "services/review";
import {
  Review as ReviewProto,
  ReviewStatus,
  ReviewType,
} from "protogen/review_pb";
import Loading from "components/common/Loading";
import Breadcrumbs from "components/common/Breadcrumbs";
import { Check, EyeOff, Plus } from "lucide-react";
import useIsMobile from "components/hooks/useIsMobile";
import AddReviewDialog from "components/review/AddReviewDialog";
import SearchAdvisorSelect from "components/common/SearchAdvisorSelect";

type ViewMode = "pending" | "all";

export default () => {
  const isMobile = useIsMobile();
  const [reviews, setReviews] = useState<ReviewProto[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>("pending");
  const [selectAdvisorOpen, setSelectAdvisorOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedAdvisorRef, setSelectedAdvisorRef] = useState<string | null>(
    null,
  );

  const { request: listReviews } = useAdminListReviews((response) => {
    if (response?.reviews) {
      setReviews(response.reviews);
    }
    setLoading(false);
  });

  const { request: updateReview } = useUpdateReview(() => {
    setLoading(true);
    if (viewMode === "pending") {
      listReviews({ status: ReviewStatus.PENDING });
    } else {
      listReviews({});
    }
  });

  useEffect(() => {
    setLoading(true);
    if (viewMode === "pending") {
      listReviews({ status: ReviewStatus.PENDING });
    } else {
      listReviews({});
    }
  }, [viewMode]);

  const handleApprove = (reviewId: string) => {
    updateReview({
      ref: reviewId,
      status: ReviewStatus.APPROVED,
    });
  };

  const handleHide = (reviewId: string) => {
    updateReview({
      ref: reviewId,
      status: ReviewStatus.HIDDEN,
    });
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Box sx={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      <Breadcrumbs
        breadcrumbs={[
          { name: "Tools", link: "/tools" },
          { name: "Reviews", link: "/tools/reviews" },
        ]}
      />

      <Box sx={{ marginTop: "24px", marginBottom: "16px" }}>
        <Typography variant="h2">Reviews</Typography>
        <Typography variant="body" sx={{ color: "#666", marginTop: "8px" }}>
          Review and moderate advisor reviews submitted by members
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          gap: isMobile ? "12px" : "24px",
          marginBottom: "16px",
        }}
      >
        <ButtonGroup
          style={{
            display: "flex",
            ...(isMobile && {
              width: "100%",
            }),
          }}
        >
          <Button
            sx={{ flex: "1", width: "100%" }}
            variant={
              viewMode === "pending" ? "buttonGroupSelected" : "buttonGroup"
            }
            onClick={() => setViewMode("pending")}
          >
            Pending
          </Button>
          <Button
            sx={{ flex: "1", width: "100%" }}
            variant={viewMode === "all" ? "buttonGroupSelected" : "buttonGroup"}
            onClick={() => setViewMode("all")}
          >
            All
          </Button>
        </ButtonGroup>
        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={() => setSelectAdvisorOpen(true)}
        >
          Add Review
        </Button>
      </Box>

      {reviews.length === 0 ? (
        <Paper
          sx={{
            padding: "48px",
            textAlign: "center",
            backgroundColor: "#F9F9F9",
          }}
        >
          <Typography variant="body" sx={{ color: "#666" }}>
            {viewMode === "pending"
              ? "No pending reviews to moderate"
              : "No reviews found"}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {reviews.map((review) => (
            <Paper
              key={review.ref}
              sx={{
                padding: "24px",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "stretch" : "flex-start",
                  gap: "16px",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      marginBottom: "8px",
                    }}
                  >
                    <Typography variant="bodyHeavy">
                      {review.displayName} reviewed {review.subjectName}
                    </Typography>
                    <Chip
                      label={
                        review.status === ReviewStatus.PENDING
                          ? "Pending"
                          : review.status === ReviewStatus.APPROVED
                            ? "Approved"
                            : "Hidden"
                      }
                      size="small"
                      sx={{
                        backgroundColor:
                          review.status === ReviewStatus.PENDING
                            ? "#FFA500"
                            : review.status === ReviewStatus.APPROVED
                              ? "#4CAF50"
                              : "#9E9E9E",
                        color: "white",
                        fontSize: "12px",
                      }}
                    />
                    <Chip
                      label={
                        review.reviewType === ReviewType.CLIENT
                          ? "Client"
                          : review.reviewType === ReviewType.ADMIN
                            ? "Admin"
                            : review.reviewType === ReviewType.SELF
                              ? "Advisor"
                              : "Unknown"
                      }
                      size="small"
                      sx={{
                        backgroundColor:
                          review.reviewType === ReviewType.CLIENT
                            ? "#2196F3"
                            : review.reviewType === ReviewType.ADMIN
                              ? "#9C27B0"
                              : review.reviewType === ReviewType.SELF
                                ? "#FF9800"
                                : "#757575",
                        color: "white",
                        fontSize: "12px",
                      }}
                    />
                  </Box>
                  <Typography
                    variant="bodySmall"
                    sx={{ color: "#666", marginBottom: "8px" }}
                  >
                    {review.createdSec &&
                      new Date(
                        Number(review.createdSec) * 1000,
                      ).toLocaleDateString()}
                  </Typography>
                  <Rating value={review.starCount} readOnly size="small" />
                  <Typography
                    variant="body"
                    sx={{ marginTop: "12px", lineHeight: "1.6" }}
                  >
                    {review.text}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: "8px",
                    flexShrink: 0,
                    ...(isMobile && { justifyContent: "flex-end" }),
                  }}
                >
                  {viewMode === "pending" ? (
                    <>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<Check size={16} />}
                        onClick={() => handleApprove(review.ref)}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<EyeOff size={16} />}
                        onClick={() => handleHide(review.ref)}
                      >
                        Hide
                      </Button>
                    </>
                  ) : (
                    <>
                      {review.status !== ReviewStatus.APPROVED && (
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          startIcon={<Check size={16} />}
                          onClick={() => handleApprove(review.ref)}
                        >
                          Approve
                        </Button>
                      )}
                      {review.status !== ReviewStatus.HIDDEN && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<EyeOff size={16} />}
                          onClick={() => handleHide(review.ref)}
                        >
                          Hide
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      <Dialog
        open={selectAdvisorOpen}
        onClose={() => {
          setSelectAdvisorOpen(false);
          setSelectedAdvisorRef(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Select Advisor to Review</DialogTitle>
        <DialogContent>
          <Box sx={{ paddingTop: "8px" }}>
            <SearchAdvisorSelect
              selectedAdvisor={selectedAdvisorRef}
              onChange={(ref) => setSelectedAdvisorRef(ref)}
              title="Advisor"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSelectAdvisorOpen(false);
              setSelectedAdvisorRef(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (selectedAdvisorRef) {
                setSelectAdvisorOpen(false);
                setAddDialogOpen(true);
              }
            }}
            disabled={!selectedAdvisorRef}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {selectedAdvisorRef && (
        <AddReviewDialog
          open={addDialogOpen}
          onClose={(created) => {
            setAddDialogOpen(false);
            setSelectedAdvisorRef(null);
            if (created) {
              setLoading(true);
              if (viewMode === "pending") {
                listReviews({ status: ReviewStatus.PENDING });
              } else {
                listReviews({});
              }
            }
          }}
          advisorRef={selectedAdvisorRef}
          isAdmin={true}
        />
      )}
    </Box>
  );
};
